import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  InputAdornment,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import { useGetCatalogJurisdictionsQuery } from '../../standardsCatalogApi';
import { useRecentSources } from '../../hooks/useRecentSources';
import { SkeletonRows, SourceIcon } from '../atoms';

import type { CspJurisdiction } from '@/shared/types/userStandard';

export interface PickedSource {
  id: string;
  title: string;
  type: string | null;
}

interface SourceCardsProps {
  onPickSource: (source: PickedSource) => void;
}

/** Well-known frameworks to surface as "common sources" (matched against jurisdiction titles). */
const COMMON_SOURCE_TITLES = ['Common Core', 'Next Generation Science', 'NGSS', 'ISTE'];

const TYPE_GROUPS: { key: string; label: string }[] = [
  { key: 'state', label: 'States' },
  { key: 'organization', label: 'Organizations' },
  { key: 'school', label: 'Schools & districts' },
  { key: 'other', label: 'Other' },
];

function toPicked(j: CspJurisdiction): PickedSource {
  return { id: j.id, title: j.title, type: j.jurisdiction_type };
}

function SourceCard({ source, onClick }: { source: PickedSource; onClick: () => void }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ p: 2, height: '100%' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            <SourceIcon type={source.type} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {source.title}
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export function SourceCards({ onPickSource }: SourceCardsProps) {
  const { data: jurisdictions = [], isLoading } = useGetCatalogJurisdictionsQuery();
  const { recent } = useRecentSources();
  const [browseAll, setBrowseAll] = useState(false);
  const [filter, setFilter] = useState('');

  const byId = useMemo(() => new Map(jurisdictions.map((j) => [j.id, j])), [jurisdictions]);

  const recentSources = useMemo(
    () =>
      recent
        .map((r) =>
          byId.has(r.id) ? toPicked(byId.get(r.id)!) : { id: r.id, title: r.title, type: null }
        )
        .slice(0, 6),
    [recent, byId]
  );

  const commonSources = useMemo(() => {
    const matched: PickedSource[] = [];
    const seen = new Set<string>();
    for (const j of jurisdictions) {
      const title = j.title.toLowerCase();
      if (COMMON_SOURCE_TITLES.some((k) => title.includes(k.toLowerCase())) && !seen.has(j.id)) {
        seen.add(j.id);
        matched.push(toPicked(j));
      }
    }
    return matched.slice(0, 6);
  }, [jurisdictions]);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const visible = q
      ? jurisdictions.filter((j) => j.title.toLowerCase().includes(q))
      : jurisdictions;
    const groups = new Map<string, CspJurisdiction[]>();
    for (const j of visible) {
      const key = ['state', 'organization', 'school'].includes(j.jurisdiction_type ?? '')
        ? (j.jurisdiction_type as string)
        : 'other';
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(j);
    }
    return groups;
  }, [jurisdictions, filter]);

  if (isLoading) {
    return (
      <Box sx={{ p: 2.5 }}>
        <SkeletonRows count={6} />
      </Box>
    );
  }

  if (browseAll) {
    return (
      <Box sx={{ p: 2.5, height: '100%', overflow: 'auto' }}>
        <Stack spacing={2} sx={{ maxWidth: 760 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setBrowseAll(false)}>
              Back
            </Button>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              All sources
            </Typography>
          </Box>
          <TextField
            size="small"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter sources"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          {TYPE_GROUPS.map(({ key, label }) => {
            const list = grouped.get(key) ?? [];
            if (list.length === 0) return null;
            return (
              <Box key={key}>
                <Typography variant="overline" color="text.secondary">
                  {label} ({list.length})
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {list.map((j) => (
                    <ListItemButton
                      key={j.id}
                      onClick={() => onPickSource(toPicked(j))}
                      sx={{ borderRadius: 1, gap: 1 }}
                    >
                      <SourceIcon type={j.jurisdiction_type} fontSize="small" color="action" />
                      <Typography variant="body2">{j.title}</Typography>
                    </ListItemButton>
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5, height: '100%', overflow: 'auto' }}>
      <Stack spacing={3} sx={{ maxWidth: 760 }}>
        {recentSources.length > 0 && (
          <Box>
            <Typography variant="overline" color="text.secondary">
              Recently used
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                gap: 1.5,
                mt: 1,
              }}
            >
              {recentSources.map((s) => (
                <SourceCard key={s.id} source={s} onClick={() => onPickSource(s)} />
              ))}
            </Box>
          </Box>
        )}

        {commonSources.length > 0 && (
          <Box>
            <Typography variant="overline" color="text.secondary">
              Common sources
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                gap: 1.5,
                mt: 1,
              }}
            >
              {commonSources.map((s) => (
                <SourceCard key={s.id} source={s} onClick={() => onPickSource(s)} />
              ))}
            </Box>
          </Box>
        )}

        <Box>
          <Button
            variant="outlined"
            startIcon={<TravelExploreIcon />}
            onClick={() => setBrowseAll(true)}
          >
            Browse all sources
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default SourceCards;
